/**
 * Microsites Slice
 * Manages microsites list, stats, filters, and sorting
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import type { MicrositesState, MicrositesFilters, MicrositesSorting } from '../types';
import type {
    MicrositeListParams,
    MicrositeListResponse,
    MicrositeStatsResponse,
    SortingFiltersResponse,
} from '../../types/api.types';
import apiClient from '../../utils/api/ApiClient';
import { API_ENDPOINTS, DEFAULT_SORT, PAGINATION_DEFAULTS } from '../../constants/api.constants';

// Initial state
const initialState: MicrositesState = {
    microsites: [],
    stats: null,
    pagination: null,
    filters: {},
    sorting: {
        sortValue: DEFAULT_SORT,
    },
    loading: 'idle',
    error: null,
};

// ============================================
// Async Thunks
// ============================================

/**
 * Fetch microsites list with filters, sorting, and pagination
 */
export const fetchMicrosites = createAsyncThunk(
    'microsites/fetchList',
    async (params: MicrositeListParams = {}) => {
        const queryParams: MicrositeListParams = {
            skip: params.skip ?? PAGINATION_DEFAULTS.SKIP,
            limit: params.limit ?? PAGINATION_DEFAULTS.LIMIT,
            sortValue: params.sortValue ?? DEFAULT_SORT,
            ...params,
        };

        const response = await apiClient.get<MicrositeListResponse>(API_ENDPOINTS.MICROSITE.LIST, {
            params: queryParams,
        });

        return response.data;
    }
);

/**
 * Fetch microsite statistics
 */
export const fetchMicrositeStats = createAsyncThunk(
    'microsites/fetchStats',
    async (storeBrand?: string) => {
        const params = storeBrand ? { storeBrand } : {};

        const response = await apiClient.get<MicrositeStatsResponse>(
            API_ENDPOINTS.MICROSITE.STATS,
            { params }
        );

        return response.data;
    }
);

/**
 * Fetch sorting and filter options
 */
export const fetchSortingFilters = createAsyncThunk('microsites/fetchSortingFilters', async () => {
    const response = await apiClient.get<SortingFiltersResponse>(
        API_ENDPOINTS.MICROSITE.SORTING_FILTERS
    );

    return response.data;
});

// ============================================
// Slice
// ============================================

const micrositesSlice = createSlice({
    name: 'microsites',
    initialState,
    reducers: {
        /**
         * Set filters
         */
        setFilters: (state, action: { payload: MicrositesFilters; type: string }) => {
            state.filters = { ...state.filters, ...action.payload };
        },

        /**
         * Set sorting
         */
        setSorting: (state, action: { payload: MicrositesSorting; type: string }) => {
            state.sorting = { ...state.sorting, ...action.payload };
        },

        /**
         * Clear all filters
         */
        clearFilters: (state) => {
            state.filters = {};
        },

        /**
         * Clear error
         */
        clearMicrositesError: (state) => {
            state.error = null;
        },

        /**
         * Reset microsites state
         */
        resetMicrositesState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            // Fetch microsites list
            .addCase(fetchMicrosites.pending, (state) => {
                state.loading = 'loading';
                state.error = null;
            })
            .addCase(fetchMicrosites.fulfilled, (state, action) => {
                state.loading = 'success';
                state.microsites = action.payload.microsites;
                state.pagination = action.payload.pagination;
                state.error = null;
            })
            .addCase(fetchMicrosites.rejected, (state, action) => {
                state.loading = 'error';
                state.error = action.error.message || 'Failed to fetch microsites';
            })

            // Fetch microsite stats
            .addCase(fetchMicrositeStats.pending, (state) => {
                state.error = null;
            })
            .addCase(fetchMicrositeStats.fulfilled, (state, action) => {
                state.stats = action.payload;
                state.error = null;
            })
            .addCase(fetchMicrositeStats.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to fetch microsite stats';
            })

            // Fetch sorting filters (no state update needed, just fetching metadata)
            .addCase(fetchSortingFilters.fulfilled, (state) => {
                state.error = null;
            })
            .addCase(fetchSortingFilters.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to fetch sorting filters';
            });
    },
});

// ============================================
// Actions
// ============================================

export const { setFilters, setSorting, clearFilters, clearMicrositesError, resetMicrositesState } =
    micrositesSlice.actions;

// ============================================
// Selectors (Custom Hooks)
// ============================================

/**
 * Get microsites list
 */
export const useMicrositesList = () => {
    return useSelector((state: RootState) => state.microsites.microsites);
};

/**
 * Get microsite statistics
 */
export const useMicrositesStats = () => {
    return useSelector((state: RootState) => state.microsites.stats);
};

/**
 * Get pagination info
 */
export const useMicrositesPagination = () => {
    return useSelector((state: RootState) => state.microsites.pagination);
};

/**
 * Get current filters
 */
export const useMicrositesFilters = () => {
    return useSelector((state: RootState) => state.microsites.filters);
};

/**
 * Get current sorting
 */
export const useMicrositesSorting = () => {
    return useSelector((state: RootState) => state.microsites.sorting);
};

/**
 * Get loading state
 */
export const useMicrositesLoading = () => {
    return useSelector((state: RootState) => state.microsites.loading);
};

/**
 * Get error
 */
export const useMicrositesError = () => {
    return useSelector((state: RootState) => state.microsites.error);
};

/**
 * Get entire microsites state
 */
export const useMicrositesState = () => {
    return useSelector((state: RootState) => state.microsites);
};

// ============================================
// Reducer
// ============================================

export default micrositesSlice.reducer;
